import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  HostListener,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  Router,
  RouterModule,
  RouterOutlet,
  NavigationEnd,
  ActivatedRoute,
} from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { RequestsFacade } from '../../../store/requests/requests.facade';
import { filter } from 'rxjs/operators';
import { Permissions } from '../../../core/enums';

interface NavItem {
  label: string;
  route: string;
  icon: string;
  badge?: number;
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

interface BreadcrumbItem {
  label: string;
  route?: string;
}

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [CommonModule, RouterModule, RouterOutlet],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
})
export class MainLayoutComponent implements OnInit {
  readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly requestsFacade = inject(RequestsFacade);
  private readonly destroyRef = inject(DestroyRef);

  private readonly TABLET_BREAKPOINT = 1024;
  sidebarCollapsed = true;
  breadcrumbItems: BreadcrumbItem[] = [];

  get navSections(): NavSection[] {
    const items: NavItem[] = [];
    if (
      this.authService.hasPermissionSync([
        Permissions.AccessRequestsListView4Customers,
        Permissions.AccessRequestsListView4Internals,
      ])
    ) {
      items.push({ label: 'Requests', route: '/requests', icon: 'requests' });
    }
    if (
      this.authService.hasPermissionSync([
        Permissions.AccessControlPanelUsersMenu,
        Permissions.AccessUserParentMenu,
      ])
    ) {
      items.push({ label: 'Users', route: '/users', icon: 'users' });
    }
    if (
      this.authService.hasPermissionSync([
        Permissions.AccessControlPanelCompaniesMenu,
      ])
    ) {
      items.push({ label: 'Company', route: '/company', icon: 'company' });
    }
    if (this.authService.hasDiagnosticPagePermission()) {
      items.push({
        label: 'Diagnostics',
        route: '/diagnostics',
        icon: 'diagnostics',
      });
    }
    return [{ items }];
  }

  ngOnInit(): void {
    this.checkScreenSize();
    this.setupBreadcrumbs();
    this.authService.diagnosticPageAccess$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(enabled => {
        if (!enabled && this.router.url.startsWith('/diagnostics')) {
          this.router.navigate(['/unauthorized']);
        }
      });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
  }

  private setupBreadcrumbs(): void {
    this.router.events
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.updateBreadcrumbs();
        }
      });
    this.updateBreadcrumbs();
  }

  private updateBreadcrumbs(): void {
    const breadcrumbs: BreadcrumbItem[] = [];

    let route = this.activatedRoute.firstChild;

    while (route) {
      const breadcrumb = route.snapshot.data['breadcrumb'];
      const breadcrumbParent = route.snapshot.data['breadcrumbParent'];

      if (breadcrumb) {
        if (breadcrumbParent) {
          const parentLabel = this.getLabelForRoute(breadcrumbParent);
          const parentExists = breadcrumbs.some(b => b.label === parentLabel);
          if (!parentExists) {
            breadcrumbs.push({
              label: parentLabel,
              route: `/${breadcrumbParent}`,
            });
          }
        }

        breadcrumbs.push({ label: breadcrumb });
      }

      route = route.firstChild;
    }

    this.breadcrumbItems = breadcrumbs;
  }

  private getLabelForRoute(route: string): string {
    const labelMap: Record<string, string> = {
      requests: 'Requests',
      users: 'Users',
      company: 'Company',
    };
    return labelMap[route] || route;
  }

  private checkScreenSize(): void {
    const isTabletOrSmaller = window.innerWidth <= this.TABLET_BREAKPOINT;
    if (isTabletOrSmaller && !this.sidebarCollapsed) {
      this.sidebarCollapsed = true;
    }
  }

  isActiveRoute(route: string): boolean {
    return this.router.url === route;
  }

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  onLogout(): void {
    this.authService.logout().subscribe(() => {
      this.router.navigate(['/login']);
    });
  }

  getUserInitials(firstName: string, lastName: string): string {
    return `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.toUpperCase();
  }

  onLogoClick(): void {
    const homeRoute = this.authService.getHomeRoute();

    if (this.router.url === homeRoute) {
      if (homeRoute === '/requests') {
        this.requestsFacade.refresh();
      }
      return;
    }

    this.router.navigate([homeRoute]);
  }
}
